// « Options de la commande » on the order page (owner, 2026-10-14).
//
// « fix those button — when i check it it doesn't add the price to the total
// price automatically ». The box used to mean « the assistant may propose
// it » and added nothing. Now:
//
//   ☑ ticked   = the client TAKES it. The worker
//                (POST /sales/orders/:id/addons/:productId) marks it accepted
//                and adds its price for THIS order to the total at once —
//                « +79 MAD » Relief on a Solo, « +200 MAD » express on a Duo.
//   ☐ unticked = taken back out, and the total comes back down.
//
// The box moves at once and goes back if the worker refuses (paid in full, a
// total corrected by hand, a parcel already shipped, a removal below what was
// already paid, a Relief bought on the website) — with the worker's own French
// reason under the card. The order's Prix / À encaisser refresh on success.
//
// Letting the assistant PROPOSE an option without adding it still exists —
// the small « Laisser l'assistant le proposer » link (a `suggested` row, as
// before). The assistant still pitches it once, at the right moment, never
// during a complaint.
//
// NOT THE SAME THING AS `OrderUpsellsCard` above it: that one lists the
// add-ons bought on the website (Shopify lines).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { salesApi, type Order, type OrderAddonsView, type OrderUpsellRow, type SetOrderAddonResult } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { usePermissions } from '@/lib/permissionsStore';
import { upsellPriceLabel, upsellSellable } from '@/lib/upsellPrice';

type UpsellsData = { upsells: OrderUpsellRow[]; addons?: OrderAddonsView | null };

export function OrderAddonsCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canAdd = can('orders.price_correction');
  const key = ['order-upsells', order.id];

  const { data: cat } = useQuery({ queryKey: ['sales-upsells'], queryFn: () => salesApi.upsells() });
  const { data: mine } = useQuery({
    queryKey: key,
    queryFn: () => salesApi.orderUpsells(order.id),
    enabled: Boolean(order.id),
  });

  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<SetOrderAddonResult | null>(null);

  const refreshOrder = () => {
    // The header's Prix / À encaisser, the AMEEX card's default, the notes.
    qc.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    qc.invalidateQueries({ queryKey: ['order-pricing', shopifyOrderId] });
    qc.invalidateQueries({ queryKey: ['order-admin-notes', shopifyOrderId] });
    qc.invalidateQueries({ queryKey: ['admin-orders'] });
  };

  const toggle = useMutation({
    mutationFn: ({ pid, on }: { pid: string; on: boolean }) => salesApi.setOrderAddon(order.id, pid, on),
    // Optimistic: the box moves now…
    onMutate: async ({ pid, on }) => {
      setError(null);
      setLast(null);
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<UpsellsData>(key);
      if (before?.addons) {
        const taken = new Set(before.addons.taken);
        if (on) taken.add(pid); else taken.delete(pid);
        qc.setQueryData<UpsellsData>(key, { ...before, addons: { ...before.addons, taken: [...taken] } });
      }
      return { before };
    },
    // …and goes back, with the worker's reason, if it was refused.
    onError: (e, _v, ctx) => {
      if (ctx?.before) qc.setQueryData(key, ctx.before);
      setError((e as Error).message || t('L’option n’a pas pu être enregistrée — réessayez.'));
    },
    onSuccess: (res) => {
      setLast(res);
      refreshOrder();
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  const propose = useMutation({
    mutationFn: (ids: string[]) => salesApi.setOrderUpsells(order.id, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const rows = mine?.upsells ?? [];
  const addons = mine?.addons ?? null;
  // Unreadable per-order view → what the option rows say (accepted = taken).
  const taken = new Set(addons?.taken ?? rows.filter((r) => r.state === 'accepted').map((r) => r.product_id));
  const locked = new Set(addons?.locked ?? []);
  const stateOf = (pid: string) => rows.find((r) => r.product_id === pid)?.state ?? null;
  const suggested = rows.filter((r) => r.state === 'suggested').map((r) => r.product_id);

  // Finished products only (a price and a Shopify link — lib/upsellPrice.ts),
  // plus anything the order already carries, so it can always be taken off.
  const options = (cat?.products ?? []).filter(
    (p) => p.kind === 'upsell' && ((upsellSellable(p) && p.enabled) || taken.has(p.id)),
  );
  if (!options.length) return null;

  const busy = toggle.isPending || propose.isPending;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="order-addons-card">
      <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
        {t('Options de la commande')}
      </h3>
      <p className="text-[11.5px] text-gray-500 mb-3 leading-snug">
        {t('Cocher = le client prend l’option : elle est ajoutée à la commande et son prix au total, tout de suite. Décocher la retire.')}
      </p>

      <ul className="space-y-2.5">
        {options.map((p) => {
          const isTaken = taken.has(p.id);
          const isLocked = locked.has(p.id);
          const st = stateOf(p.id);
          const forOrder = addons?.prices?.[p.id];
          const priceText = forOrder != null && forOrder > 0
            ? `+${forOrder} MAD`
            : upsellPriceLabel(p, t);
          const disabled = !canAdd || busy || isLocked || (!isTaken && forOrder != null && !(forOrder > 0));
          return (
            <li key={p.id} className="text-[12.5px]">
              <label className={`flex items-start gap-2.5 ${disabled ? '' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  aria-label={p.title}
                  disabled={disabled}
                  checked={isTaken}
                  onChange={(e) => toggle.mutate({ pid: p.id, on: e.target.checked })}
                  title={!canAdd ? t('Réservé aux personnes qui peuvent corriger le prix') : undefined}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-gray-800">{p.title}</span>
                    <span className={`tabular-nums font-semibold ${isTaken ? 'text-green-700' : 'text-gray-500'}`}>{priceText}</span>
                    {isTaken && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">
                        {isLocked ? t('pris sur le site') : t('Ajouté à la commande')}
                      </span>
                    )}
                  </span>
                  {!isTaken && (
                    <span className="block mt-0.5 text-[11px] text-gray-500">
                      {st === 'offered' ? t('L’assistant l’a proposé — en attente de la réponse.')
                        : st === 'declined' ? t('Refusé ou retiré — l’assistant ne le reproposera pas.')
                          : (
                            <button
                              type="button"
                              disabled={busy}
                              className="underline decoration-dotted underline-offset-2 hover:text-gray-700 disabled:opacity-50 cursor-pointer"
                              onClick={(e) => {
                                e.preventDefault();
                                propose.mutate(st === 'suggested'
                                  ? suggested.filter((x) => x !== p.id)
                                  : [...suggested, p.id]);
                              }}
                            >
                              {st === 'suggested'
                                ? t('✓ L’assistant peut le proposer (sans l’ajouter) — annuler')
                                : t('Laisser l’assistant le proposer')}
                            </button>
                          )}
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {error && (
        <p role="alert" className="text-[11.5px] text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">
          {error}
        </p>
      )}
      {last && !last.unchanged && (
        <p className="text-[11.5px] text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-3">
          {last.on
            ? t('« {titre} » ajouté : total {avant} → {apres} MAD.', { titre: last.title, avant: last.total_before, apres: last.total })
            : t('« {titre} » retiré : total {avant} → {apres} MAD.', { titre: last.title, avant: last.total_before, apres: last.total })}
          {last.ameex_parcel_open && (
            <span className="block mt-1 text-amber-800">
              {t('Le colis AMEEX est déjà créé avec l’ancien montant : cliquez « Modifier » sur la carte AMEEX pour envoyer le nouveau.')}
            </span>
          )}
        </p>
      )}
      {[...taken].some((id) => !locked.has(id)) && (
        <p className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
          {t('⚠️ La ligne Shopify n’est pas ajoutée automatiquement : ajoutez-la à la main sur la commande Shopify.')}
        </p>
      )}
    </div>
  );
}
